<template>
  <div>
    <b-sidebar
      id="sidebar-edit-contract"
      sidebar-class="sidebar-lg"
      :visible="changePhysicalFranchiseSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @change="(val) => mutateChangePhysicalFranchiseSidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h4>Alterar escritório do consultor</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="onSubmit"
          @reset.prevent="resetForm"
        >
          <div class="mb-2">
            <h5>{{ changePhysicalFranchiseSidebar.name }}</h5>
            <h6>{{ changePhysicalFranchiseSidebar.physicalFranchiseName }}</h6>
          </div>

          <b-form-group label="Novo escritório" label-for="consultant-new-franchise">
            <v-select
              id="consultant-new-franchise"
              v-model="newPhysicalFranchiseId"
              required
              :reduce="(physicalFranchise) => physicalFranchise.id"
              :options="physicalFranchises"
              label="name"
              :loading="loadingPhysicalFranchises"
              :class="getSelectErrorClass(v$.newPhysicalFranchiseId.$error)"
            >
             <template #no-options="{ search, searching, loading }">
                Não há escritórios disponíveis para este consultor
              </template>
            </v-select>
            <div class="invalid-feedback">
              <span v-if="v$.newPhysicalFranchiseId.required.$invalid">
                Você deve informar um novo escritório para o consultor
              </span>
            </div>
          </b-form-group>

          <b-form-group label="Motivo da alteração" label-for="consultant-new-franchise-reason">
            <b-form-textarea
              id="consultant-new-franchise-reason"
              v-model="reason"
              rows="3"
              :state="v$.reason.$error ? false : null"
            />
            <div class="invalid-feedback">
              <span v-if="v$.reason.required.$invalid">
                Você deve informar o motivo da alteração de escritório
              </span>
              <span v-else-if="v$.reason.minLength.$invalid">
                O motivo deve ter ao menos 5 caracteres
              </span>
            </div>
          </b-form-group>

          <b-form-group label="Número do chamado (se houver)" label-for="consultant-new-franchise-ticket">
            <b-form-input
              id="consultant-new-franchise-ticket"
              v-model="ticketNumber"
            />
          </b-form-group>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              :disabled="saving"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
            >
              <span>
                {{ saving ? "Salvando..." : "Salvar" }}
              </span>
            </b-button>
          </div>
        </b-form>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import _ from "lodash";
import {
  BSidebar,
  BForm,
  BFormGroup,
  BButton,
  BFormInput,
  BFormTextarea,
} from "bootstrap-vue";
import { mapActions, mapMutations, mapGetters } from "vuex";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import useVuelidate from "@vuelidate/core";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import * as types from "../store/types";
import * as sharedTypes from "@/modules/shared/store/types";
import { required, minLength } from "@vuelidate/validators";
import { getVueSelectErrorClass } from "@/helpers/validators";

export default {
  components: {
    // BSV
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      saving: false,
      loadingPhysicalFranchises: false,
      newPhysicalFranchiseId: undefined,
      reason: "",
      ticketNumber: "",
    };
  },
  computed: {
    ...mapGetters({
      changePhysicalFranchiseSidebar: types.CHANGE_PHYSICAL_FRANCHISE_SIDEBAR,
      physicalFranchises: sharedTypes.PHYSICAL_FRANCHISES,
    }),
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() };
  },
  validations() {
    return {
      newPhysicalFranchiseId: { required },
      reason: { required, minLength: minLength(5) },
    };
  },
  mounted() {
    this.loadingPhysicalFranchises = true;
    this.getPhysicalFranchises()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar os dados para seleção. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loadingPhysicalFranchises = false;
      });
  },
  methods: {
    ...mapMutations({
      mutateChangePhysicalFranchiseSidebar:
        types.MUTATE_CHANGE_PHYSICAL_FRANCHISE_SIDEBAR,
    }),
    ...mapActions({
      changePhysicalFranchise: types.CHANGE_PHYSICAL_FRANCHISE,
      getPhysicalFranchises: sharedTypes.GET_PHYSICAL_FRANCHISES,
    }),
    async onSubmit() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;
      this.saving = true;

      await this.changePhysicalFranchise({
        consultant_id: this.changePhysicalFranchiseSidebar.id,
        physical_franchise_id: this.newPhysicalFranchiseId,
        reason: this.reason,
        ticket_number: this.ticketNumber || null
      })
        .then((response) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso",
              text: "Alteração de escritório registrada com sucesso!",
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
          this.mutateChangePhysicalFranchiseSidebar({ visible: false });
          this.changePhysicalFranchiseSidebar.saveAction();
        })
        .catch((error) => {
          if (error.response && error.response.status === 400) {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text: error.response.data.error,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          } else {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text: "Ocorreu um erro ao atualizar o escritório do consultor. Entre em contato com o setor de TI.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
    getSelectErrorClass(thereIsError) {
      return getVueSelectErrorClass(thereIsError);
    },
    clear() {
      this.newPhysicalFranchiseId = undefined;
      this.reason = "";
      this.ticketNumber = "";
      this.v$.$reset();
    },
  },
};
</script>
